import React from 'react';
import { Check, X } from 'lucide-react';
import './Privacy.css';

export default function PrivacyPage(): React.ReactElement {
  return (
    <div className="privacy-page">
      <div className="privacy-container">
        <header className="privacy-header">
          <h1 className="privacy-title">Hereabout - Privacy Policy</h1>
          <p className="privacy-date">Last Updated: 9/10/2025</p>
        </header>

        <section className="privacy-section">
          <h2>Privacy at a Glance</h2>
          <div className="glance-grid">
            <div className="glance-item">
              <strong>Our Promise</strong>
              <p>We respect your privacy and only collect what's needed to make the app work.</p>
            </div>
            <div className="glance-item">
              <strong>Your Control</strong>
              <p>You decide who sees your posts - public, following-only, or private.</p>
            </div>
            <div className="glance-item">
              <strong>Your Data</strong>
              <p>Stored securely on EU servers. No selling to advertisers or data brokers.</p>
            </div>
            <div className="glance-item">
              <strong>Your Rights</strong>
              <p>Delete individual posts, download all your data, or delete everything.</p>
            </div>
          </div>
        </section>

        <section className="privacy-section">
          <h2>What We Collect & Why</h2>

          <div className="subsection">
            <h3>Account Information</h3>
            <p><strong>What:</strong> Username, email, profile picture</p>
            <p><strong>Why:</strong> So you can log in and others can recognize you</p>
            <p><strong>Your Control:</strong> Update anytime in settings</p>
          </div>

          <div className="subsection">
            <h3>Location Data</h3>
            <p><strong>What:</strong> GPS coordinates when you drop pins</p>
            <p><strong>Why:</strong> To show your posts on the map</p>
            <p><strong>Your Control:</strong> Choose privacy level for each post, delete location history</p>
          </div>

          <div className="subsection">
            <h3>Your Content</h3>
            <p><strong>What:</strong> Photos, text, voice recordings you share</p>
            <p><strong>Why:</strong> To display your posts to your chosen audience</p>
            <p><strong>Your Control:</strong> Delete individual posts or everything</p>
          </div>

          <div className="subsection">
            <h3>Technical Stuff</h3>
            <p><strong>What:</strong> Device type, app crashes, usage patterns</p>
            <p><strong>Why:</strong> To fix bugs and improve the app</p>
            <p><strong>Note:</strong> This data is anonymized</p>
          </div>
        </section>

        <section className="privacy-section">
          <h2>How We Use Your Information</h2>

          <h3>Things We Do</h3>
          <div className="table-container">
            <table className="privacy-table">
              <tbody>
                <tr className="table-header-row">
                  <td><strong>Use</strong></td>
                  <td><strong>Public Posts</strong></td>
                  <td><strong>Following-Only</strong></td>
                  <td><strong>Private Posts</strong></td>
                </tr>
                <tr>
                  <td>Show to intended audience</td>
                  <td><Check size={20} color="#22c55e" /></td>
                  <td><Check size={20} color="#22c55e" /></td>
                  <td><Check size={20} color="#22c55e" /></td>
                </tr>
                <tr>
                  <td>Marketing materials</td>
                  <td><Check size={20} color="#22c55e" /></td>
                  <td><X size={20} color="#ef4444" /></td>
                  <td><X size={20} color="#ef4444" /></td>
                </tr>
                <tr>
                  <td>App improvement</td>
                  <td><Check size={20} color="#22c55e" /></td>
                  <td><Check size={20} color="#22c55e" /></td>
                  <td><Check size={20} color="#22c55e" /></td>
                </tr>
                <tr>
                  <td>Technical backups</td>
                  <td><Check size={20} color="#22c55e" /></td>
                  <td><Check size={20} color="#22c55e" /></td>
                  <td><Check size={20} color="#22c55e" /></td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3>Things We Never Do</h3>
          <ul>
            <li>Sell your personal information</li>
            <li>Share your data with advertisers</li>
            <li>Use private content for marketing</li>
            <li>Track your location continuously</li>
            <li>Read your private messages</li>
          </ul>
        </section>

        <section className="privacy-section">
          <h2>Who We Share With</h2>

          <div className="subsection">
            <h3>Inside the App</h3>
            <ul>
              <li><strong>Public posts:</strong> Everyone can see them</li>
              <li><strong>Following-only posts:</strong> Just your followers</li>
              <li><strong>Private posts:</strong> Only you</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Outside the App (Limited Cases)</h3>
            <ul>
              <li><strong>Google Firebase:</strong> For push notifications (industry standard)</li>
              <li><strong>Cloud storage:</strong> Secure backup of your content</li>
              <li><strong>Legal requirements:</strong> Only if required by law</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Never Shared</h3>
            <ul>
              <li>Your private content</li>
              <li>Your personal information for marketing</li>
              <li>Your data with data brokers</li>
            </ul>
          </div>
        </section>

        <section className="privacy-section">
          <h2>How We Protect Your Data</h2>

          <div className="subsection">
            <h3>Security Measures</h3>
            <ul>
              <li><strong>EU servers:</strong> Strictest privacy laws in the world</li>
              <li><strong>Encryption:</strong> Your data is encrypted in storage and transit</li>
              <li><strong>Limited access:</strong> Only authorized employees can access user data</li>
              <li><strong>Regular audits:</strong> We constantly test our security</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Your Role in Security</h3>
            <ul>
              <li>Use a strong password</li>
              <li>Don't share your account</li>
              <li>Adjust privacy settings as desired</li>
              <li>Report suspicious activity</li>
            </ul>
          </div>
        </section>

        <section className="privacy-section">
          <h2>Your Privacy Controls</h2>

          <div className="subsection">
            <h3>Post Privacy Levels</h3>
            <ul>
              <li><strong>Public:</strong> Everyone sees it, we can use for marketing</li>
              <li><strong>Following-Only:</strong> Only your followers see it</li>
              <li><strong>Private:</strong> Only you see it</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Location Privacy</h3>
            <ul>
              <li>Choose privacy level when creating posts</li>
              <li>View post history on your profile</li>
              <li>Delete individual location data</li>
              <li>Delete all location data</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Account Privacy</h3>
            <ul>
              <li>Control who can follow you</li>
              <li>Set default privacy levels</li>
              <li>Manage notification preferences</li>
            </ul>
          </div>
        </section>

        <section className="privacy-section">
          <h2>Your Data Rights</h2>

          <div className="rights-grid">
            <div className="right-card">
              <h3>Access Your Data</h3>
              <p><strong>What:</strong> Get a copy of everything we have about you</p>
              <p><strong>How:</strong> Email <a href="mailto:engineering@stormbyte.dev">engineering@stormbyte.dev</a></p>
              <p><strong>When:</strong> Within 30 days</p>
            </div>

            <div className="right-card">
              <h3>Correct Your Data</h3>
              <p><strong>What:</strong> Fix incorrect information</p>
              <p><strong>How:</strong> Update in app settings or email us</p>
              <p><strong>When:</strong> Immediately (in app) for most things</p>
            </div>

            <div className="right-card">
              <h3>Delete Your Data</h3>
              <p><strong>What:</strong> Remove posts, location data, messages, or entire account</p>
              <p><strong>How:</strong> In-app deletion or email support</p>
              <p><strong>When:</strong> Immediate hiding, complete deletion within 30 days</p>
              <p><strong>Note:</strong> Individual message deletion coming in future update</p>
            </div>

            <div className="right-card">
              <h3>Export Your Data</h3>
              <p><strong>What:</strong> Portable copy of your posts and data</p>
              <p><strong>How:</strong> Email <a href="mailto:engineering@stormbyte.dev">engineering@stormbyte.dev</a></p>
              <p><strong>When:</strong> Within 30 days</p>
            </div>
          </div>
        </section>

        <section className="privacy-section">
          <h2>How Long We Keep Your Data</h2>

          <div className="subsection">
            <h3>While You're Active</h3>
            <ul>
              <li><strong>Posts:</strong> Until you delete them</li>
              <li><strong>Account info:</strong> While your account exists</li>
              <li><strong>Location data:</strong> Until you delete associated posts</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>After You Delete Things</h3>
            <ul>
              <li><strong>Individual posts:</strong> Hidden immediately, deleted within 30 days</li>
              <li><strong>Account deletion:</strong> All data deleted within 30 days</li>
              <li><strong>Backups:</strong> Completely removed within 90 days</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Special Cases</h3>
            <ul>
              <li><strong>Livestreams:</strong> Not stored after they end</li>
              <li><strong>Legal holds:</strong> May retain longer if legally required</li>
            </ul>
          </div>
        </section>

        <section className="privacy-section">
          <h2>International Users</h2>

          <div className="subsection">
            <h3>Where Your Data Lives</h3>
            <ul>
              <li><strong>Primary storage:</strong> EU servers</li>
              <li><strong>Backups:</strong> Also in EU or equivalent protection jurisdictions</li>
              <li><strong>Why EU:</strong> Strongest privacy protections globally</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Your Rights by Location</h3>
            <ul>
              <li><strong>EU users:</strong> Full GDPR rights</li>
              <li><strong>California users:</strong> CCPA rights</li>
              <li><strong>Everyone else:</strong> Same protections as EU users (because it's simpler)</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Exercising Rights</h3>
            <p>Email <a href="mailto:engineering@stormbyte.dev">engineering@stormbyte.dev</a> with "Privacy Request" in the subject line.</p>
          </div>
        </section>

        <section className="privacy-section">
          <h2>Age & Privacy</h2>

          <div className="subsection">
            <h3>Age Requirement</h3>
            <p><strong>18+ only.</strong> This isn't negotiable for a location-sharing app.</p>
          </div>

          <div className="subsection">
            <h3>What If We Find Younger Users?</h3>
            <ul>
              <li>Account immediately deactivated</li>
              <li>All data deleted</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>Why 18+?</h3>
            <p>Location sharing has inherent safety risks. We'd rather be overly cautious.</p>
          </div>
        </section>

        <section className="privacy-section">
          <h2>Third-Party Services</h2>

          <div className="subsection">
            <h3>Google Firebase</h3>
            <p><strong>What it does:</strong> Push notifications, user authentication</p>
            <p><strong>Data shared:</strong> Device tokens, user IDs</p>
            <p><strong>Why necessary:</strong> Industry standard for mobile notifications</p>
            <p><strong>Their policy:</strong> <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Google Privacy Policy</a></p>
          </div>

          <div className="subsection">
            <h3>Cloud Storage Providers</h3>
            <p><strong>What they do:</strong> Secure backup of your content</p>
            <p><strong>Data shared:</strong> Encrypted content only</p>
            <p><strong>Access:</strong> No direct access to your content</p>
          </div>

          <div className="subsection">
            <h3>Analytics (Anonymized)</h3>
            <p><strong>What:</strong> Usage patterns, crash reports</p>
            <p><strong>Data:</strong> No personal identifiers included</p>
            <p><strong>Purpose:</strong> Fix bugs, improve features</p>
          </div>
        </section>

        <section className="privacy-section">
          <h2>Changes to This Policy</h2>

          <div className="subsection">
            <h3>How We Handle Updates</h3>
            <ul>
              <li><strong>Major changes:</strong> In-app notification + email</li>
              <li><strong>Minor changes:</strong> Updated date + in-app notice</li>
            </ul>
          </div>

          <div className="subsection">
            <h3>What Counts as Major?</h3>
            <ul>
              <li>Changes to data sharing practices</li>
              <li>New types of data collection</li>
              <li>Changes to your rights</li>
              <li>New third-party services</li>
            </ul>
          </div>
        </section>

        <section className="privacy-section">
          <h2>Privacy FAQ</h2>

          <div className="faq-list">
            <div className="faq-item">
              <h4>Is my location always tracked?</h4>
              <p><strong>No.</strong> Only when you create a post with location.</p>
            </div>

            <div className="faq-item">
              <h4>Can I use the app without sharing location?</h4>
              <p><strong>No.</strong> Location is core to the app's full functionality. You can, however, browse posts as a guest without having to share any location.</p>
            </div>

            <div className="faq-item">
              <h4>What happens to my data if Hereabout shuts down?</h4>
              <p>You'll get 90 days notice to download your data.</p>
            </div>

            <div className="faq-item">
              <h4>Can police access my data?</h4>
              <p>Only with proper legal process (warrant/subpoena).</p>
            </div>

            <div className="faq-item">
              <h4>Do you use my data for AI training?</h4>
              <p>Not currently, and we'd ask permission first.</p>
            </div>

            <div className="faq-item">
              <h4>Can I see what data you have about me?</h4>
              <p><strong>Yes.</strong> Email <a href="mailto:engineering@stormbyte.dev">engineering@stormbyte.dev</a> for a complete copy.</p>
            </div>
          </div>
        </section>

        <section className="privacy-section">
          <h2>Contact Us</h2>

          <div className="contact-grid">
            <div className="contact-card">
              <h3>Privacy Questions</h3>
              <p><strong>Email:</strong> <a href="mailto:engineering@stormbyte.dev">engineering@stormbyte.dev</a></p>
              <p><strong>Subject:</strong> Privacy Question</p>
              <p><strong>Response time:</strong> Within 48 hours</p>
            </div>

            <div className="contact-card">
              <h3>Data Requests</h3>
              <p><strong>Email:</strong> <a href="mailto:engineering@stormbyte.dev">engineering@stormbyte.dev</a></p>
              <p><strong>Subject:</strong> Privacy Request</p>
              <p><strong>Include:</strong> What you want (copy, deletion, correction)</p>
              <p><strong>Response time:</strong> Within 30 days</p>
            </div>

            <div className="contact-card">
              <h3>General Questions</h3>
              <p><strong>Email:</strong> <a href="mailto:engineering@stormbyte.dev">engineering@stormbyte.dev</a></p>
            </div>
          </div>
        </section>

        <footer className="privacy-footer">
          <p>This Privacy Policy is effective as of 9/10/2025 and applies to all users globally.</p>
        </footer>
      </div>
    </div>
  );
}
